import { pintHttp, cosmosHttp } from '@api/client';
import { paramterize, pickResponse } from '@api/utils';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import * as Pint from './pint';
import Axios from 'axios-observable';
import { type } from 'os';
import { windowObj } from '@lib/common';
import { http, http_v2 } from '@api/client';
import { compact } from 'lodash';

const urlMap = {
  person: '/api/v1/deli/cmd/person/search?sort_key=projects_count&sort=desc',
  company: '/api/v1/deli/cmd/company/search',
  content: '/api/v1/cmd/content/search',

  normalize: d => {
    if (Array.isArray(d.data)) {
      d.data = { data: d.data };
    }
    return d;
  },
} as const;

const v2UrlMap = {
  person: '/api/v1/deli/cmd/person/search',
  company: '/api/v2/cmd/company/search',
  content: '/api/v2/cmd/content/search',
  normalize: d => d,
} as const;

const activeUrlMap = urlMap;

export function search(
  type: 'content',
  query: Pint.SearchParams
): Observable<Pint.ContentSearchReponse>;
export function search(
  type: 'company',
  query: Pint.SearchParams
): Observable<Pint.CompanySearchResponse>;
export function search(
  type: 'person',
  query: Pint.SearchParams
): Observable<Pint.PersonSearchResponse>;
export function search<K extends 'company' | 'content' | 'person'>(
  type: K,
  query: Pint.SearchParams
) {
  if (type === 'content') {
    query.sort_key = query.sort_key ? query.sort_key : 'popularity';
    query.sort = query.sort ? query.sort : 'desc';
    (query as any).name = query.query ? query.query.trim() : null;
    delete query.query;
  }
  return pintHttp
    .get(activeUrlMap[type], {
      params: paramterize(query),
    })
    .pipe(
      catchError(err => {
        if (err.response?.data?.data?.length === 0) {
          return Promise.resolve(err.response);
        }
        return Promise.reject(err);
      }),
      pickResponse(),
      map(activeUrlMap.normalize)
    );
}

export function getPintContentById({
  id,
  sort_key = 'popularity',
  sort = 'desc',
}: {
  id: string | string[];
  sort_key?: string;
  sort?: 'asc' | 'desc';
}) {
  const content_vtr_ids = typeof id === 'string' ? id : id.join(',');

  return pintHttp
    .get<Pint.ContentSearchReponse>('/api/v1/cmd/content/search', {
      params: { sort_key, sort, content_vtr_ids },
    })
    .pipe(pickResponse(), pickResponse());
}

export function getPintContent(id: string) {
  return pintHttp
    .get<Pint.PaginatedResponse<Pint.Content>>(`/api/v1/cmd/content/${id}`)
    .pipe(pickResponse(), pickResponse());
}

/**
 * Autocomplete from locationIQ
 */
export function getGeoLocationAutoCompleteFromLocationIq(searchString: string) {
  return Axios.get<Pint.CommonGeoCode[]>(
    `${windowObj.RAZZLE_LOCATION_IQ_API}/v1/autocomplete.php?key=${windowObj.RAZZLE_LOCATION_IQ_API_KEY}&q=${searchString}&tag=place:city,place:state,place:country,place:district&limit=20&dedupe=1`
  ).pipe(
    map(response =>
      response['data'].map(data => {
        return {
          latitude: data['lat'],
          longitude: data['lon'],
          display_value: data['display_name'],
          country: data['address']['country'] ? data['address']['country'] : '',
          city: data['address']['city'] ? data['address']['city'] : '',
          state: data['address']['state'] ? data['address']['state'] : '',
          type: (data['type'] as string).toLowerCase(),
        };
      })
    )
  );
}

/**
 * Autocomplete from Mapquest
 */
export function getGeoLocationAutoCompleteFromMapQuest(searchString: string) {
  return Axios.get<Pint.CommonGeoCode[]>(
    `${windowObj.RAZZLE_MAP_QUEST_API}/geocoding/v1/address?key=${windowObj.RAZZLE_MAP_QUEST_API_KEY}&location=${searchString}`
  ).pipe(
    map(response => {
      return response['data']['results'][0]['locations'].map(data => {
        return {
          latitude: data['latLng']['lat'],
          longitude: data['latLng']['lng'],
          display_value:
            (data['adminArea5'] ? data['adminArea5'] + ', ' : '') +
            (data['adminArea3'] ? data['adminArea3'] + ', ' : '') +
            (data['adminArea1'] ? data['adminArea1'] : ''),
          country: data['adminArea1'] ? data['adminArea1'] : '',
          state: data['adminArea3'] ? data['adminArea3'] : '',
          city: data['adminArea5'] ? data['adminArea5'] : '',
          type: (data['geocodeQuality'] as string).toLowerCase(),
        };
      });
    })
  );
}

/**
 * Autocomplete from  position stack
 */
export function getGeoLocationAutoCompleteFromPositionStack(searchString: string) {
  return Axios.get<Pint.CommonGeoCode[]>(
    `${windowObj.RAZZLE_POSITION_STACK_API}/v1/forward?access_key=${windowObj.RAZZLE_POSITION_STACK_API_KEY}&query=${searchString}`
  ).pipe(
    map(response => {
      return response['data']['data'].map(data => {
        return {
          latitude: data['latitude'],
          longitude: data['longitude'],
          display_value: data['label'],
          country: data['country'] ? data['country'] : '',
          state: data['region'] ? data['region'] : '',
          city: data['locality'] ? data['locality'] : '',
          type: (data['type'] as string).toLowerCase(),
        };
      });
    })
  );
}

const idValueMapping = (x: { id?: any; value?: { $fuzzy?: any; $exact?: any; $range?: any } }) => {
  if (x.id == 'query') {
    if (x.value.$exact) return { query: x.value.$exact, search_type: 'exact' };
    if (x.value.$fuzzy) return { query: x.value.$fuzzy, search_type: 'fuzzy' };
  }
  if (x.value.$exact) return { name: x.id, search_type: 'exact', value: x.value.$exact };
  if (x.value.$fuzzy) return { name: x.id, search_type: 'fuzzy', value: x.value.$fuzzy };
  if (x.value.$range)
    return {
      name: x.id,
      search_type: 'range',
      value: x.value.$range.toString().length != 4 ? x.value.$range : x.value.$range + '-01-01',
    };
};

/**
 *
 */
export function cosmosSearch(params: any) {
  
  const updated_arr = []
  const updated_params = params?.filters.map((mapping_filter) => {
    const arr = {}
    const id = mapping_filter['id']
    const val = mapping_filter['value']
    arr[id] = val
    return (arr)
  })



  const getValueFromKeyInArray = (array, key) => { 
    return array.find(obj => obj[key])?.[key]; 
  }
  const pageSize = params.pageSize ? params.pageSize : 10;
  const pageIndex = params.page ? params.page : 0;
  const queryFilter = params.filters.filter(filters => filters['id'] === 'query');
  const nonQueryFilters = params.filters.filter(filters => filters['id'] !== 'query');
  let query: any = { query: '', search_type: 'fuzzy' };
  let nonQuery = [];

  if (queryFilter && queryFilter.length > 0) {
    query = idValueMapping(queryFilter[0]);
  }
  if (nonQueryFilters) {
    nonQuery = nonQueryFilters.map(x => idValueMapping(x));
  }


  if(getValueFromKeyInArray(updated_params, "vtr_id") != null || getValueFromKeyInArray(updated_params, "content_title") != null){
    
    let filters = {
      'page_size': 10,
      'page': 1,
      
    }
    if(getValueFromKeyInArray(updated_params, "vtr_id") != null) {
      var vtr_id = Object.values(getValueFromKeyInArray(updated_params, "vtr_id"))[0]
      filters['vtr_id'] = '$eq|||'+vtr_id
    }
    if(getValueFromKeyInArray(updated_params, "content_title") != null) {
      var param_filter = params.filters.filter(person => person.id == 'content_title')[0].value;
      // param_filter = param_filter?.map(key => param_filter[key]).join(',')
      
      var key = Object.keys(param_filter)[0];
      
      var val = Object.values(param_filter)[0];
      if(key == '$fuzzy'){
        key = '$contains';
      }
      const filter_condition = key+'|||'+val;
      var content_title = Object.values(getValueFromKeyInArray(updated_params, "content_title"))[0]
      filters['title'] = filter_condition
    }
    
    
    return http_v2.get(`/content-cosmos`, ({

        params: filters
      }))
      .pipe(
        catchError(err => {
          if (err.response?.data?.data?.length === 0) {
            return Promise.resolve(err.response);
          }
          return Promise.reject(err);
        }),
        pickResponse(),
        map(x =>
          ({
            data: x['data']['data']['contents'],
            page_size: x['data']['_pages']['limit'] - 1,
            count: x['data']['_pages']['total'],
          })
        )
      );
  }else {
    return cosmosHttp
    .post(`/api/v1/cosmos/search`, {
      ...query,
      filters: nonQuery,
      control: {
        sort_key: '_score,popularity',
        sort: 'desc',
        start:
          parseInt(pageSize) * (parseInt(pageIndex) - 1)
            ? parseInt(pageSize) * (parseInt(pageIndex) - 1)
            : 0,
        limit: parseInt(pageSize),
      },
    })
    .pipe(
      catchError(err => {
        if (err.response?.data?.data?.length === 0) {
          return Promise.resolve(err.response);
        }
        return Promise.reject(err);
      }),
      pickResponse(),
      map(x =>
        ({
          data: x['data']['contents'],
          page_size: x['_pages']['limit'] - 1,
          count: x['_pages']['total'],
        })
      )
    );
  } 
}
